import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemPLDecisionModal } from '@/types/modal';
import { useGetPLDecisions, useItem, useSubmitPLDecision } from '@/hooks/item';
import { ErrorModal, StandardModal, NoFooterModal, SuccessModal } from '@/components/molecules/common/Modal';
import { Dropdown, Input } from '@/components/molecules/common/Form';
import styles from './Modal.module.scss';
import { Skeleton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/types/query';

const MODAL_WIDTH = 550;

const ItemPLDecision: React.FC<{ modal: ItemPLDecisionModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const queryClient = useQueryClient();

  const itemR = useItem(item.productId, false);

  const decisionRes = useGetPLDecisions(item.productId);

  const submitR = useSubmitPLDecision();

  const [decisionType, setDecisionType] = useState('');

  const [action, setAction] = useState('');

  const [refundComment, setRefundComment] = useState('');

  const title = t('item.postal_litigation.plDecision.title');

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
      queryClient.invalidateQueries({ queryKey: [QueryKeys.POSTAL_LITIGATION] });
    }
  }, [submitR.kind]);

  const data = decisionRes.kind === 'Loaded' ? decisionRes.data : [];

  const decisionTypes = useMemo(() => {
    if (data.length) {
      return data
        .filter((d) => !!d.type && !!d.label)
        .map((d) => ({
          label: d.label || '',
          value: d.type || '',
        }));
    }
    return [];
  }, [data]);

  const actions = useMemo(() => {
    if (data.length && !!decisionType) {
      const selectedDecision = data.find((d) => d.type === decisionType);

      return selectedDecision && selectedDecision.decisionValues?.length
        ? selectedDecision.decisionValues
            .filter((a) => !!a.decisionValue && !!a.label)
            .map((a) => ({
              label: a.label || '',
              value: a.decisionValue || '',
              needRefundComment: a.needRefundComment || null,
            }))
        : [];
    }
    return [];
  }, [data, decisionType]);

  const isPLDecisionLoading = decisionRes.kind === 'Called' && decisionRes.fetching;

  const isPLError = decisionRes.kind === 'Error';

  const isSubmissionError = submitR.kind === 'Error';

  const isShowRefundComment = actions.find((_) => _.value === action)?.needRefundComment;

  if (submitR.kind === 'Loading' || isPLDecisionLoading) {
    return (
      <NoFooterModal
        title={title}
        body={
          <Skeleton
            width={MODAL_WIDTH}
            height={80}
            variant="rounded"
          />
        }
        onClose={onClose}
        modalName="pl_decision"
      />
    );
  }
  if (isSubmissionError || isPLError) {
    const submissionError = isSubmissionError ? submitR.error : undefined;
    const plError = isPLError ? decisionRes.error : undefined;

    return (
      <ErrorModal
        title={title}
        error={submissionError || plError}
        onClose={onClose}
        useOriginMsg={true}
      />
    );
  }

  if (submitR.kind === 'Completed') {
    return (
      <SuccessModal
        title={title}
        onClose={onClose}
      />
    );
  }

  const handleDecisionChange = (value: string) => {
    setAction('');
    setDecisionType(value);
  };

  const body = (
    <div className={styles.containerGap}>
      <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
      <div
        className={styles.text1rem}
        dangerouslySetInnerHTML={{
          __html: t('item.postal_litigation.plDecision.description'),
        }}
      />
      <Dropdown
        required={true}
        label={t('item.postal_litigation.plDecision.decisionType')}
        value={decisionType}
        onSelect={handleDecisionChange}
        items={decisionTypes}
      />
      <Dropdown
        required={true}
        label={t('item.postal_litigation.plDecision.action')}
        value={action}
        onSelect={setAction}
        items={actions}
      />
      {isShowRefundComment && (
        <Input
          rows={4}
          value={refundComment}
          onChange={setRefundComment}
          label={t('item.postal_litigation.plDecision.refundComment')}
          placeholder={t('item.postal_litigation.plDecision.refundCommentPlaceholder')}
          required={true}
          multiline={true}
          InputLabelProps={{ shrink: true }}
        />
      )}
    </div>
  );

  const onSubmit = () => {
    submitR.mutate({
      productId: item.productId,
      action,
      type: decisionType,
      decisionComment: refundComment,
    });
  };

  return (
    <StandardModal
      title={title}
      onClose={onClose}
      onSubmit={onSubmit}
      submittable={!!decisionType && !!action && (!isShowRefundComment || !!refundComment)}
      body={body}
      containerStyle={{ maxWidth: MODAL_WIDTH }}
      modalName="pl_confirmation"
      submitLabel={t('item.postal_litigation.plDecision.submit')}
    />
  );
};

export default ItemPLDecision;
